
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type SortDirection = 'asc' | 'desc';

export interface SortConfig<T> {
  key: keyof T | null;
  direction: SortDirection;
}

// Generic sort function that can be used across the application
export function sortData<T>(data: T[], config: SortConfig<T>): T[] {
  if (!config.key) return data;
  
  return [...data].sort((a, b) => {
    const aValue = a[config.key as keyof T];
    const bValue = b[config.key as keyof T];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return config.direction === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return config.direction === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    }
    
    // Handle boolean values
    if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      return config.direction === 'asc'
        ? (aValue === bValue ? 0 : aValue ? 1 : -1)
        : (aValue === bValue ? 0 : aValue ? -1 : 1);
    }
    
    // Default comparison for other types
    if (aValue < bValue) return config.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return config.direction === 'asc' ? 1 : -1;
    return 0;
  });
}

// Function to calculate pagination bounds
export function getPaginatedData<T>(data: T[], currentPage: number, itemsPerPage: number): T[] {
  return data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
}

// Function to calculate total pages
export function getTotalPages(totalItems: number, itemsPerPage: number): number {
  return Math.ceil(totalItems / itemsPerPage);
}

// Debouncer class
export class Debouncer {
  private timeoutId: number | null = null; // Changed NodeJS.Timeout to number
  private readonly delay: number;

  constructor(delay: number) {
    this.delay = delay;
  }

  public debounce(func: () => void): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = window.setTimeout(func, this.delay); // Use window.setTimeout for browser environment
  }

  public cancel(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
